import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { RegistrationLayout } from "./RegistrationFlow";
import { completeRegistrationSession } from "./registrationSession";
import { getErrorMessage, isApiError, isSuccessResponse } from "./registrationHelpers";
import { userService } from "../services/userService";
import userController from "../controllers/userController";
import AddressPanel from "../vendorFlow/accountSettings/AddressPanel";
import BusinessLocationIcon from "../icons/Address/BusinessLocationIcon";
import { translateLabel, translateText, useTranslationSnapshot } from "../translations/runtime";

export default function RegistrationAddress({ mode = "vendor" }) {
  useTranslationSnapshot();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: true, error: "" });
  const isDelivery = mode === "delivery";
  const finish = useCallback(() => {
    navigate(isDelivery ? "/register/delivery/success" : "/register/success", { replace: true });
  }, [isDelivery, navigate]);

  useEffect(() => {
    let active = true;
    setState({ loading: true, error: "" });
    const prepare = async () => {
      try {
        const session = await completeRegistrationSession({ isDelivery });
        if (!active) return;
        const response = await userController.getUserAddress({
          userID: session.UserID, jwt: userService.getJwt(),
        });
        if (isApiError(response) || !isSuccessResponse(response)) throw response;
        const data = response?.data?.data ?? response?.data?.result ?? response?.data;
        const addresses = Array.isArray(data) ? data : data?.addresses;
        if (!Array.isArray(addresses)) throw new Error("Unable to check saved addresses. Please retry.");
        if (!active) return;
        if (addresses.length) finish();
        else setState({ loading: false, error: "" });
      } catch (error) {
        if (active) setState({ loading: false, error: getErrorMessage(error, "Unable to prepare your account. Please retry.") });
      }
    };
    prepare();
    return () => { active = false; };
  }, [attempt, finish, isDelivery]);

  if (!userService.getJwt()) return <Navigate to="/sign-in" replace />;
  return (
    <RegistrationLayout
      title={translateLabel("txt_web_vendor_add_business_address", "Add Your Business Address")}
      subtitle={translateLabel("txt_web_vendor_business_address_setup_description", "Provide your business address to complete your account setup and continue.")}
      icon={<BusinessLocationIcon />}
      showBack={false}
    >
      {state.loading ? <p role="status">{translateLabel("txt_web_vendor_preparing_account", "Preparing your account...")}</p> :
        state.error ? <>
          <p role="alert">{translateText(state.error)}</p>
          <button type="button" className="registration-primary" onClick={() => setAttempt(value => value + 1)}>
            {translateLabel("btn_retry", "Retry")}
          </button>
        </> : <AddressPanel mode="registration" onSaved={finish} />}
    </RegistrationLayout>
  );
}
